// pages/SignalerDocumentPage.tsx
import React, { useState, useContext } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { UserContext } from "features/account";
import Button from 'shared/ui/Button';
import Select from 'shared/ui/Select';
import Input from 'shared/ui/Input';
import Alert from 'shared/ui/Alert';
import ReportedDocumentsTable from 'features/signaler-document/components/ReportedDocumentsTable/ReportedDocumentsTable';
import { reportDocument } from "features/signaler-document";
import SelectCountryEU from 'shared/ui/SelectCountryEU';
import './SignalerDocumentPage.scss';
import DocumentIllustration from "components/DocumentIllustration";

interface FormValues {
  paysEmetteur: string;
  typeDocument: string;
  numeroDocument: string;
  motif: string;
}

const documentTypeOptions = [
  { value: "passport", label: "Passeport" },
  { value: "id_card", label: "Carte d'identité" },
  { value: "residency_permit", label: "Carte de séjour" },
  { value: "driver_permit", label: "Permis de conduire" },
];

const motifOptions = [
  { value: 'Fraude avérée', label: 'Fraude avérée' },
  { value: "Usurpé", label: "Usurpation d'identité" },
  { value: 'Perte/Vol', label: 'Perte/Vol' },
];

const SignalerDocumentPage: React.FC = () => {
  const { user } = useContext(UserContext);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTable, setRefreshTable] = useState(false);
  const [countryCode, setCountryCode] = useState('FRA');

  const initialValues: FormValues = {
    paysEmetteur: 'FRA',
    typeDocument: '',
    numeroDocument: '',
    motif: '',
  };

  const validationSchema = Yup.object({
    paysEmetteur: Yup.string().required('Le pays émetteur est requis'),
    typeDocument: Yup.string().required('Le type de document est requis'),
    numeroDocument: Yup.string()
      .required('Le numéro de document est requis')
      .min(5, 'Le numéro de document doit contenir au moins 5 caractères')
      .max(20, 'Le numéro de document ne doit pas dépasser 20 caractères'),
    motif: Yup.string().required('Le motif est requis'),
  });

  const handleSubmit = async (values: FormValues, { resetForm }: any) => {
    setError(null);
    setSubmitted(false);

    try {
      const response = await reportDocument({
        paysEmetteur: values.paysEmetteur,
        typeDocument: values.typeDocument,
        numeroDocument: values.numeroDocument,
        motif: values.motif,
        companyName: user?.companyName || null,
      });

      if (response.success) {
        setSubmitted(true);
        resetForm();
        setCountryCode('FRA');
        setRefreshTable(prev => !prev);
        setTimeout(() => setSubmitted(false), 5000);
      }
     else {
        setError(response?.error || 'Une erreur est survenue');
        setTimeout(() => setError(null), 5000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Une erreur est survenue');
      setTimeout(() => setError(null), 5000);
    }
  };

  return (
    <div className="signaler-document-page">
      <div className="container">
        <div className="row">
          <div className="col-md-6">
            <div className="card">
              <h3 className="">Signaler un document frauduleux</h3>
              <div className="card-body">
                {submitted && (
                  <Alert type="success">Votre signalement a été enregistré avec succès !
                  </Alert>
                )}

                {error && (
                  <Alert type="error">
                    {error}
                  </Alert>
                )}

                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ isSubmitting, values, setFieldValue, handleChange }) => (
                    <Form>
                      <div className="d-flex flex-column align-items-end justify-content-between h-100">
                        <div className="w-100">
                          <div className="d-flex gap-1 mb-3 flex-wrap">
                            {/* Pays émetteur with SelectCountryEU */}
                            <div className="">
                              <div className="d-flex gap-1 mb-3 min-w-20">
                                <Field
                                  name="paysEmetteur"
                                  value={values.paysEmetteur}
                                  id="paysEmetteur"
                                  onChange={handleChange}
                                >
                                  {({ field }: any) => (
                                    <SelectCountryEU
                                      field={field}
                                      country={countryCode}
                                      handleChange={(value: string) => {
                                        setCountryCode(value);
                                        setFieldValue("paysEmetteur", value);
                                      }}
                                    />
                                  )}
                                </Field>
                              </div>
                              <ErrorMessage name="paysEmetteur" component="div" className="text-danger small mt-1" />
                            </div>

                            {/* Type de document */}
                            <div className="mb-3 min-w-60">
                              <Field
                                name="typeDocument"
                                value={values.typeDocument}
                                id="typeDocument"
                                onChange={handleChange}
                              >
                                {({ field }: any) => (
                                  <Select
                                    field={field}
                                    placeholder="Type de document"
                                    options={documentTypeOptions}
                                    handleChange={(value: string) => {
                                      setFieldValue("typeDocument", value);
                                    }}
                                  />
                                )}
                              </Field>
                              <ErrorMessage name="typeDocument" component="div" className="text-danger small mt-1" />
                            </div>
                          </div>

                          {/* Numéro de document */}
                          <div className="form-group mb-3">
                            <Input
                              type="text"
                              name="numeroDocument"
                              id="numeroDocument"
                              placeholder="Numéro du document"
                              value={values.numeroDocument}
                              onChange={(e: any) => {
                                setFieldValue("numeroDocument", e.target.value.toUpperCase());
                              }}
                              className="form-control"
                            />
                            <ErrorMessage name="numeroDocument" component="div" className="text-danger small mt-1" />
                          </div>

                          {/* Motif */}
                          <div className="form-group mb-4">
                            <label htmlFor="motif">Motif du signalement *</label>
                            <Field
                              name="motif"
                              value={values.motif}
                              id="motif"
                              onChange={handleChange}
                            >
                              {({ field }: any) => (
                                <Select
                                  field={field}
                                  placeholder=""
                                  options={motifOptions}
                                  handleChange={(value: string) => {
                                    setFieldValue("motif", value);
                                  }}
                                />
                              )}
                            </Field>
                            <ErrorMessage name="motif" component="div" className="text-danger small mt-1" />
                          </div>

                          <Button
                            buttonType="submit"
                            label="Signaler le document"
                            disabled={isSubmitting}
                            className="btn btn-primary w-100"
                          />
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card information-card">
              <div className="d-flex justify-content-center align-items-center">
                <DocumentIllustration />
              </div>
            </div>
          </div>
        </div>

        {/* Display reported documents table */}
        {user?.type === 'partner' && (
          <div className="row mt-5">
            <div className="col-12">
              <ReportedDocumentsTable
                companyName={user?.companyName ?? ''}
                refreshTrigger={refreshTable}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignalerDocumentPage;